import { AutorenewOutlined, LockOutlined } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { CUSTOM, formatSignRange } from 'src/sections/monthly-closeout-alert/customCloseout';

type Props = {
  //  `location.state` from the alert view — the period's date range plus the closeout status and
  //  division forwarded from the Closeout Alerts row.
  dateRange: any;
};

/**
 * What the panel calls the period it is talking about.
 *
 * A monthly closeout is named by its month, the way the mock and the ticket's `{month}` token
 * assume. A custom range is not a month and must not be described as one — naming its calendar
 * month would tell a DOR that all of July was invoiced when only Jul 6–19 was — so it is named by
 * its dates instead. Anything unusable degrades to a neutral subject rather than "Invalid date".
 */
export const financialPeriodSubject = (dateRange: any): string => {
  if (dateRange?.division === CUSTOM) {
    return formatSignRange(dateRange?.startDate, dateRange?.endDate) ?? 'This period';
  }
  const start = dateRange?.startDate ? moment(dateRange.startDate) : moment.invalid();
  return start.isValid() ? start.format('MMMM YYYY') : 'This period';
};

/** "…to review or approve for this month" only reads right when the period *is* a month. */
export const financialPeriodNoun = (dateRange: any): string =>
  dateRange?.division === CUSTOM ? 'period' : 'month';

/**
 * Path B of BOPS-170 — the period was invoiced and finance has closed the books, so there is no
 * preliminary review to open.
 *
 * This panel *replaces* the "Open preliminary invoice review" button rather than sitting beside a
 * disabled one: there is nothing to click, and a dead control reads as a bug the user should keep
 * trying. It sits directly under the action strip and changes nothing below it.
 */
const NoPreliminaryReviewPanel = (props: Props) => {
  const { dateRange } = props;

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        padding: '20px 24px',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
      >
        <LockOutlined
          fontSize="small"
          sx={{ color: '#6C737F' }}
        />
        <Typography variant="h6">No preliminary review for this period</Typography>
      </Stack>

      <Typography
        variant="body1"
        sx={{ color: '#6C737F', marginTop: '8px' }}
      >
        {`${financialPeriodSubject(dateRange)} was already invoiced and finance closed the ` +
          `financial period, so there is no invoice left to review or approve for this ` +
          `${financialPeriodNoun(dateRange)}.`}
      </Typography>

      {/*  BOPS-129 — the sentence carries no day count, and BOPS-170 requires it to name no
           specific month either: the adjustment window is configurable and which invoice a
           correction lands on is the detector's call (BOPS-157), not this screen's. It also opens
           on "Only" on purpose, so it cannot be read as a promise that every change carries
           forward. The mock's wording ("Your changes aren't lost … the August 2026 invoice")
           predates both rules — the acceptance criteria are the authority here. */}
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.25}
        sx={{
          backgroundColor: '#EBEEFE',
          borderRadius: '8px',
          padding: '12px 16px',
          marginTop: '16px',
        }}
      >
        <AutorenewOutlined
          fontSize="small"
          sx={{ color: '#6366F1', marginTop: '2px' }}
        />
        <Typography
          variant="body2"
          sx={{ color: '#312E81' }}
        >
          {"Only changes within the adjustment window appear on next month's invoice as a " +
            'prior-period adjustment.'}
        </Typography>
      </Stack>
    </Box>
  );
};

export default NoPreliminaryReviewPanel;
